<template>
    <div class="dropzone-wrapper">
        <label v-if="label" class="dropzone-label p-1">{{ label }}</label>
        <div
            ref="uploader"
            class="drop-zone"
            :class="{ 'drop-zone--active': isDragging }"
            @dragenter.prevent="isDragging = true"
            @dragleave.prevent="isDragging = false"
            @dragover.prevent
            @drop.prevent="isDragging = false"
        >
            <div class="drop-zone-content">
                <div class="upload-icon-circle">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                        <polyline points="16 16 12 12 8 16"/>
                        <line x1="12" y1="12" x2="12" y2="21"/>
                        <path d="M20.39 18.39A5 5 0 0 0 18 9h-1.26A8 8 0 1 0 3 16.3"/>
                    </svg>
                </div>
                <p class="drop-zone-text">
                    <span class="upload-link" @click="openFile">Click to upload</span>
                    <span> or drag and drop</span>
                </p>
                <p class="drop-zone-hint">Maximum file size 50 MB</p>
            </div>
        </div>

        <ul v-if="items.length > 0" class="file-list">
            <DropzoneItem
                v-for="item in items"
                :key="item.upload.uuid"
                :item="item"
                @item-cancel="onFileCancel"
            />
        </ul>
    </div>
</template>

<script lang="ts">
// @ts-nocheck
import JDropzone, { DropzoneFile, DropzoneOptions } from 'dropzone'
import { merge } from 'lodash'
import { Component, Model, Prop, Ref, Vue, toNative } from 'vue-facing-decorator'
import DropzoneItem from './DropzoneItem.vue'

@Component({
    components: { DropzoneItem },
    emits: ['progress', 'error', 'success', 'update:modelValue'],
})
class Dropzone extends Vue {
    dropzone!: JDropzone
    items: DropzoneFile[] = []
    isDragging = false

    @Model({ type: [String] })
    readonly value!: string

    @Prop({ type: String })
    readonly url!: string

    @Prop({ type: String, default: 'file' })
    readonly param!: string

    @Prop({ type: Object, default: () => ({}) })
    readonly options!: object

    @Prop({ type: String, default: '' })
    readonly label!: string

    @Prop({ type: Boolean, default: true })
    readonly multiple!: boolean

    @Ref('uploader')
    readonly uploader: HTMLDivElement

    get defaults(): DropzoneOptions {
        return {
            acceptedFiles: 'image/*, application/pdf, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/msword',
            disablePreviews: true,
            headers: {
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content'),
            },
            addedfile: (file) => {
                if (!this.multiple) this.items = []
                this.items.push(file)
            },
            success: (file) => {
                const response = JSON.parse(file.xhr?.response)
                const fileUrl = response?.file
                if (fileUrl) this.$emit('update:modelValue', fileUrl)
                this.$emit('success', response)
            },
            error: (file, message, xhr) => {
                this.$emit('error', { file, message, xhr })
            },
            paramName: this.param,
            url: this.url,
        }
    }

    openFile() {
        this.dropzone.hiddenFileInput.click()
    }

    onFileCancel({ file }) {
        this.dropzone.cancelUpload(file)
        this.onRemoveFile(file)
    }

    onRemoveFile(file) {
        this.dropzone.removeFile(file)
        this.items.splice(this.items.indexOf(file), 1)
    }

    mounted() {
        this.dropzone = new JDropzone(this.uploader, merge(this.defaults, this.options))
        this.dropzone.on('maxfilesexceeded', (file) => {
            this.onRemoveFile(file)
            this.$emit('error', { file, message: 'Maximum File Exceeded' })
        })
    }
}

export default toNative(Dropzone)
</script>

<style scoped lang="scss">
.dropzone-label {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: #333;
    margin-bottom: 6px;
    font-family: inherit;
}

.dropzone-wrapper {
    width: 100%;
}

.drop-zone {
    border: 1.5px dashed #d0d0d0;
    border-radius: 10px;
    cursor: pointer;
    transition: border-color 0.15s, background 0.15s;

    &--active,
    &:hover {
        border-color: #999;
        background: #fafafa;
    }
}

.drop-zone-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    padding: 24px 16px;
    text-align: center;
}

.upload-icon-circle {
    width: 40px;
    height: 40px;
    border: 1.5px solid #e0e0e0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #555;
    margin-bottom: 2px;
}

.drop-zone-text {
    font-size: 13px;
    color: #666;
    margin: 0;
    font-family: inherit;
}

.upload-link {
    color: #111;
    font-weight: 500;
    text-decoration: underline;
    text-underline-offset: 2px;
    cursor: pointer;

    &:hover {
        color: #555;
    }
}

.drop-zone-hint {
    font-size: 12px;
    color: #aaa;
    margin: 0;
    font-family: inherit;
}

.file-list {
    list-style: none;
    padding: 0;
    margin: 8px 0 0;
}
</style>